import type { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { MercatorCoordinate } from 'maplibre-gl';
import type * as THREE from 'three';
import type { GLTFLoaderPlugin, GLTFParser } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  cutHeightFor,
  geoToModel,
  groundAltitude,
  lambert93ToWgs84,
  levelOf,
  modelToGeo,
  modelToMercatorMatrix,
  modelToThree,
} from './site-model-georef';
import { NavGrid } from './nav-grid';
import { NavGraph, Point3, stairsFromTriangles } from './nav-graph';
import { DEFAULT_TRAJECTORY, followPath, TrajectorySettings } from './person-motion';
import { PersonPlanner } from './person-planner';
import { navKindOf, OUTDOOR_M } from './nav-source';
import { navGridFromNavigation, NavigationV1, parseNavigation, stairsFromNavigation } from './navigation';
import { embeddedTexturesPlugin } from './glb-embedded-textures';
import { SiteModel } from './site-model.types';

/** Tracker à montrer en personnage 3D. */
export interface BuildingPerson {
  id: string;
  color: string;
  lat: number;
  lon: number;
  alt: number;
  offline?: boolean;
  /** Heure de mesure de la position (ms epoch) ; à défaut, l'heure de réception. */
  measuredAt?: number;
}

/** Personnage : mannequin animé (repos / marche), chargé depuis les assets de l'app. */
export interface PersonModelSource {
  url: string;
  /** Hauteur affichée, en mètres. */
  heightM?: number;
}

type ThreeModule = typeof import('three');

interface Person {
  obj: THREE.Object3D;
  ring: THREE.Mesh;
  mixer: THREE.AnimationMixer;
  idle: THREE.AnimationAction;
  walk: THREE.AnimationAction;
  pos: THREE.Vector3;
  target: THREE.Vector3;
  weight: number;
  /** Rejet des points impossibles, lissage et chemin (sans three.js, testé à part). */
  planner: PersonPlanner;
  /** Points de passage restants vers `target` (par les portes et l'escalier, hauteur comprise). */
  path: Point3[];
  /** Changement d'étage sans chemin (escalier absent) : posé directement. */
  jump: boolean;
}

/** Triangles du GLB utiles à la circulation, en coordonnées du modèle three.js (y vers le haut). */
interface NavSource {
  floor: Float32Array;
  walls: Float32Array;
  doors: Float32Array;
  frames: Float32Array;
  /** Un tableau par escalier (nœud `Stairs`). */
  stairs: Float32Array[];
}

/** Durée d'affichage de la croix d'un point ignoré (s). */
const GHOST_S = 5;
/** Vitesse à laquelle l'animation de marche du mannequin est jouée à l'échelle 1. */
const WALK_SPEED_M_S = 1.4;
/** Plugin de textures typé pour GLTFLoader (il ne dépend que de ce qu'il lit du parseur). */
const texturesPlugin = (T: typeof THREE) =>
  embeddedTexturesPlugin(T) as unknown as (parser: GLTFParser) => GLTFLoaderPlugin;

export const BUILDING_LAYER_ID = 'wheere-site-building';
const LAYER_ID = BUILDING_LAYER_ID;

/**
 * Bâtiment d'un site (GLB d'export_model.py) posé à sa place exacte sur une carte MapLibre, dans
 * une couche three.js (ADR-017 point 1). three.js est chargé à la demande : il n'est téléchargé
 * que pour un site qui a un modèle.
 *
 * - `setFloor` coupe le bâtiment au-dessus de l'étage choisi (`all` : pas de coupe) ;
 * - `setPeople` place un personnage par tracker, qui marche vers chaque nouvelle position, écrêtée
 *   et lissée selon `setTrajectory` (réglages de démo) ;
 * - `project` donne la position écran d'un point lat / lon / alt (pour placer les marqueurs à
 *   leur altitude) ; `onRender` prévient à chaque image.
 */
export class BuildingLayer {
  private mainMatrix: number[] | null = null;
  private readonly people = new Map<string, Person>();
  private trajectory: TrajectorySettings = DEFAULT_TRAJECTORY;
  private navSource: NavSource | null = null;
  /** Circulation décrite par l'export (bloc `navigation` v1) ; sinon devinée dans la géométrie. */
  private navigation: NavigationV1 | null = null;
  /** Grilles de circulation par étage, pour la marge aux murs courante. */
  private readonly navGrids = new Map<string, NavGrid>();
  /** Étages reliés par les escaliers du GLB (grilles lues au besoin). */
  private navGraph: NavGraph | null = null;
  /** Dernière liste demandée : rejouée quand le mannequin finit de charger. */
  private wanted: BuildingPerson[] = [];
  private personTemplate: { scene: THREE.Object3D; clips: THREE.AnimationClip[]; scale: number } | null = null;
  private renderListeners = new Set<() => void>();
  private rejectedListeners = new Set<(personId: string) => void>();
  /** Croix grises des points ignorés, effacées en {@link GHOST_S} secondes. */
  private ghosts: { obj: THREE.Group; born: number }[] = [];
  private readonly clip: THREE.Plane;
  private readonly clock: THREE.Clock;
  private readonly peopleGroup: THREE.Group;
  private readonly modelToMerc: THREE.Matrix4;
  private readonly ground: number;
  private removed = false;
  /** Masqué : plan affiché en contours GeoJSON, le bâtiment n'est plus dessiné. */
  private hidden = false;

  private constructor(
    private readonly T: ThreeModule,
    private readonly skeleton: typeof import('three/examples/jsm/utils/SkeletonUtils.js'),
    private readonly map: MaplibreMap,
    readonly model: SiteModel,
    private readonly scene: THREE.Object3D
  ) {
    this.clip = new T.Plane(new T.Vector3(0, -1, 0), 1e6);
    this.clock = new T.Clock();
    this.peopleGroup = new T.Group();
    this.ground = groundAltitude(model.georef, model.levels);
    const [lat0, lon0] = lambert93ToWgs84(model.georef.lasOrigin[0], model.georef.lasOrigin[1]);
    const origin = MercatorCoordinate.fromLngLat([lon0, lat0]);
    this.modelToMerc = new T.Matrix4().fromArray(
      modelToMercatorMatrix(
        model.georef,
        model.levels,
        (lon, lat) => MercatorCoordinate.fromLngLat([lon, lat]),
        origin.meterInMercatorCoordinateUnits()
      )
    );
    scene.traverse(o => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) {
        return;
      }
      for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        m.clippingPlanes = [this.clip];
        m.side = T.DoubleSide;
      }
    });
  }

  /** Charge three.js, lit le GLB et ajoute la couche à la carte (sous les marqueurs HTML). */
  static async create(map: MaplibreMap, model: SiteModel, glb: ArrayBuffer): Promise<BuildingLayer> {
    const [T, { GLTFLoader }, skeleton] = await Promise.all([
      import('three'),
      import('three/examples/jsm/loaders/GLTFLoader.js'),
      import('three/examples/jsm/utils/SkeletonUtils.js'),
    ]);
    const gltf = await new GLTFLoader().register(texturesPlugin(T)).parseAsync(glb, '');
    const layer = new BuildingLayer(T, skeleton, map, model, gltf.scene);
    layer.navSource = navSourceOf(T, gltf.scene);
    const { nav, issues } = parseNavigation(
      gltf.scene.userData,
      model.levels,
      Object.fromEntries(model.rooms.map(r => [r.id, r.level]))
    );
    if (issues.length) {
      // Bloc invalide : ignoré en entier (contrat GLB), heuristiques à la place ; visible en console.
      console.warn('[site-building] bloc navigation du GLB ignoré', issues);
    }
    layer.navigation = nav;
    layer.addToMap();
    // Grilles de circulation (~150 ms par étage) calculées après l'affichage, pas au premier déplacement.
    setTimeout(() => model.levels.forEach(l => layer.navGrid(l.z)), 0);
    return layer;
  }

  /** Ajoute la couche à la carte (création, ou après un changement de fond de carte qui l'a retirée). */
  addToMap(): void {
    this.map.addLayer(this.customLayer());
  }

  /** Montre ou masque le bâtiment et ses personnages, sans retirer la couche. */
  setVisible(visible: boolean): void {
    this.hidden = !visible;
    this.map.triggerRepaint();
  }

  /** Charge le mannequin des personnages ; sans lui, pas de personnages (les marqueurs restent). */
  async loadPersonModel(source: PersonModelSource): Promise<void> {
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const gltf = await new GLTFLoader().register(texturesPlugin(this.T)).loadAsync(source.url);
    gltf.scene.updateMatrixWorld(true);
    const size = new this.T.Box3().setFromObject(gltf.scene, true).getSize(new this.T.Vector3());
    this.personTemplate = { scene: gltf.scene, clips: gltf.animations, scale: (source.heightM ?? 1.75) / size.y };
    this.setPeople(this.wanted);
  }

  /** Coupe au-dessus de l'étage `levelId` ; `all` montre le bâtiment entier. */
  setFloor(levelId: string | 'all'): void {
    this.clip.constant = Math.min(cutHeightFor(this.model.levels, levelId), 1e6);
    this.map.triggerRepaint();
  }

  /** Personnages à montrer : ajoutés, déplacés vers leur nouvelle position ou retirés. */
  setPeople(list: BuildingPerson[]): void {
    this.wanted = list;
    if (!this.personTemplate) {
      return;
    }
    const seen = new Set<string>();
    for (const p of list) {
      seen.add(p.id);
      const existing = this.people.get(p.id);
      if (existing) {
        this.retarget(p.id, existing, this.measured(p), (p.measuredAt ?? Date.now()) / 1000);
      } else {
        this.people.set(p.id, this.makePerson(p, this.floorPosition(p)));
      }
    }
    for (const [id, person] of this.people) {
      if (!seen.has(id)) {
        this.peopleGroup.remove(person.obj, person.ring);
        this.people.delete(id);
      }
    }
    this.map.triggerRepaint();
  }

  /** Réglages de démo du mouvement des personnages (écrêtage, lissage, rattrapage). */
  setTrajectory(settings: TrajectorySettings): void {
    if (settings.wallClearanceM !== this.trajectory.wallClearanceM) {
      this.navGrids.clear();
    }
    this.trajectory = settings;
    this.map.triggerRepaint();
  }

  /**
   * Nouvelle position reçue (`tS` : heure de mesure, s) : rejetée si elle est impossible à pied
   * depuis la dernière retenue, sinon le personnage la rejoint par les portes et l'escalier.
   */
  private retarget(id: string, person: Person, measured: Point3, tS: number): void {
    const step = person.planner.receive(
      measured,
      tS,
      { x: person.pos.x, y: person.pos.y, z: person.pos.z },
      this.trajectory
    );
    if (step.verdict === 'rejected') {
      this.addGhost(step.at);
      this.rejectedListeners.forEach(l => l(id));
    }
    if (!step.path) {
      return;
    }
    person.path = step.path;
    person.jump = step.jump ?? false;
    const end = step.path[step.path.length - 1];
    person.target = new this.T.Vector3(end.x, end.y, end.z);
  }

  /** Étages et escaliers du GLB, pour les chemins d'un étage à l'autre et la distance à pied. */
  private graph(): NavGraph | null {
    if (!this.navSource && !this.navigation) {
      return null;
    }
    if (!this.navGraph) {
      const levels = this.model.levels.map(l => l.z);
      const stairs = this.navigation
        ? stairsFromNavigation(this.navigation, this.model.levels)
        : (this.navSource?.stairs ?? []).flatMap(t => stairsFromTriangles(t, levels));
      this.navGraph = new NavGraph(levels, y => this.navGrid(y), stairs);
    }
    return this.navGraph;
  }

  /** Grille de circulation de l'étage dont le plancher est à `floorY` (calculée au premier besoin). */
  private navGrid(floorY: number): NavGrid | null {
    const level = this.model.levels.find(l => Math.abs(l.z - floorY) < 0.01);
    if (!level || (!this.navSource && !this.navigation)) {
      return null;
    }
    let grid = this.navGrids.get(level.id);
    if (!grid && this.navigation) {
      grid = navGridFromNavigation(this.navigation, level.id, {
        clearanceM: this.trajectory.wallClearanceM,
        roomLevels: Object.fromEntries(this.model.rooms.map(r => [r.id, r.level])),
        outdoorM: OUTDOOR_M,
      });
      this.navGrids.set(level.id, grid);
    }
    if (!grid && this.navSource) {
      const ground = Math.min(...this.model.levels.map(l => l.z));
      grid = NavGrid.build({
        floorY: level.z,
        clearanceM: this.trajectory.wallClearanceM,
        outdoorM: level.z === ground ? OUTDOOR_M : 0,
        floor: this.navSource.floor,
        walls: this.navSource.walls,
        doors: this.navSource.doors,
        frames: this.navSource.frames,
      });
      this.navGrids.set(level.id, grid);
    }
    return grid ?? null;
  }

  /** Position affichée (lissée, en marche) du premier personnage, pour le suivi de la caméra. */
  personLngLat(): [number, number] | null {
    const first = this.people.values().next().value as Person | undefined;
    if (!first) {
      return null;
    }
    const g = modelToGeo(this.model.georef, { x: first.pos.x, y: -first.pos.z, z: first.pos.y });
    return [g.lon, g.lat];
  }

  /** Hauteur des pieds du premier personnage (y three.js), pour l'étage à afficher en suivi. */
  personFeetY(): number | null {
    const first = this.people.values().next().value as Person | undefined;
    return first ? first.pos.y : null;
  }

  /** Position écran (px CSS) d'un point lat / lon / alt, ou `null` (pas encore rendu, derrière la caméra). */
  project(lat: number, lon: number, alt: number): { x: number; y: number } | null {
    const e = this.mainMatrix;
    if (!e) {
      return null;
    }
    const c = MercatorCoordinate.fromLngLat([lon, lat], alt - this.ground);
    const w = e[3] * c.x + e[7] * c.y + e[11] * c.z + e[15];
    if (w <= 0) {
      return null;
    }
    const x = (e[0] * c.x + e[4] * c.y + e[8] * c.z + e[12]) / w;
    const y = (e[1] * c.x + e[5] * c.y + e[9] * c.z + e[13]) / w;
    const canvas = this.map.getCanvas();
    return { x: ((x + 1) / 2) * canvas.clientWidth, y: ((1 - y) / 2) * canvas.clientHeight };
  }

  /**
   * Décalage écran à appliquer à un marqueur posé au sol (lat / lon) pour le montrer à son
   * altitude : position projetée à l'altitude moins position projetée au sol.
   */
  altitudeOffset(lat: number, lon: number, alt: number): [number, number] {
    const top = this.project(lat, lon, alt);
    const base = this.project(lat, lon, this.ground);
    return top && base ? [top.x - base.x, top.y - base.y] : [0, 0];
  }

  /** Prévient à chaque position ignorée (impossible à pied), pour un compteur. */
  onRejected(listener: (personId: string) => void): () => void {
    this.rejectedListeners.add(listener);
    return () => this.rejectedListeners.delete(listener);
  }

  onRender(listener: () => void): () => void {
    this.renderListeners.add(listener);
    return () => this.renderListeners.delete(listener);
  }

  remove(): void {
    this.removed = true;
    this.renderListeners.clear();
    this.rejectedListeners.clear();
    if (this.map.getLayer(LAYER_ID)) {
      this.map.removeLayer(LAYER_ID);
    }
  }

  // ── couche MapLibre ───────────────────────────────────────────────────

  private customLayer(): CustomLayerInterface {
    const T = this.T;
    let renderer: THREE.WebGLRenderer;
    const camera = new T.Camera();
    const scene = new T.Scene();
    scene.add(new T.HemisphereLight(0xffffff, 0x8890a0, 2.2));
    const sun = new T.DirectionalLight(0xffffff, 1.6);
    sun.position.set(-30, 60, 40);
    scene.add(sun, this.scene, this.peopleGroup);
    return {
      id: LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      onAdd: (map, gl) => {
        renderer = new T.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
        renderer.autoClear = false;
        renderer.localClippingEnabled = true;
      },
      render: (_gl, args: CustomRenderMethodInput) => {
        if (this.removed || this.hidden) {
          return;
        }
        this.mainMatrix = Array.from(args.defaultProjectionData.mainMatrix);
        this.updatePeople(Math.min(0.1, this.clock.getDelta()));
        camera.projectionMatrix = new T.Matrix4().fromArray(this.mainMatrix).multiply(this.modelToMerc);
        renderer.resetState();
        renderer.render(scene, camera);
        this.renderListeners.forEach(l => l());
        if (this.people.size || this.ghosts.length) {
          this.map.triggerRepaint();
        }
      },
    };
  }

  // ── personnages ───────────────────────────────────────────────────────

  private floorPosition(p: BuildingPerson): THREE.Vector3 {
    const m = geoToModel(this.model.georef, p);
    const [x, , z] = modelToThree(m);
    return new this.T.Vector3(x, levelOf(this.model.levels, m.z).z, z);
  }

  /** Position mesurée en coordonnées three.js, hauteur réelle (pas ramenée au plancher). */
  private measured(p: BuildingPerson): Point3 {
    const [x, y, z] = modelToThree(geoToModel(this.model.georef, p));
    return { x, y, z };
  }

  private makePerson(p: BuildingPerson, start: THREE.Vector3): Person {
    const T = this.T;
    const tpl = this.personTemplate as NonNullable<BuildingLayer['personTemplate']>;
    const obj = this.skeleton.clone(tpl.scene);
    obj.scale.setScalar(tpl.scale);
    obj.traverse(o => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) {
        return;
      }
      mesh.frustumCulled = false;
      const tint = (mat: THREE.Material) => {
        const c = mat.clone() as THREE.MeshStandardMaterial;
        c.clippingPlanes = [this.clip];
        // Articulations à la couleur du tracker, corps gris foncé (gris clair s'il est hors ligne).
        c.color = new T.Color(/joint/i.test(mat.name) ? p.color : p.offline ? 0x9ca3af : 0x374151);
        return c;
      };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(tint) : tint(mesh.material);
    });
    const ring = new T.Mesh(
      new T.RingGeometry(0.42, 0.62, 40),
      new T.MeshBasicMaterial({
        color: p.color,
        transparent: true,
        opacity: 0.9,
        side: T.DoubleSide,
        clippingPlanes: [this.clip],
      })
    );
    ring.rotation.x = -Math.PI / 2;
    const mixer = new T.AnimationMixer(obj);
    const action = (name: string) => {
      const c = tpl.clips.find(a => a.name === name) ?? tpl.clips[0];
      return mixer.clipAction(c);
    };
    const idle = action('idle');
    const walk = action('walk');
    idle.play();
    walk.play();
    walk.setEffectiveWeight(0);
    obj.position.copy(start);
    this.peopleGroup.add(obj, ring);
    const planner = new PersonPlanner(
      this.model.levels,
      () => (this.trajectory.walls ? this.graph() : null),
      y => this.navGrid(y),
      { x: start.x, y: start.y, z: start.z },
      (p.measuredAt ?? Date.now()) / 1000,
      this.trajectory
    );
    return {
      obj,
      ring,
      mixer,
      idle,
      walk,
      pos: start.clone(),
      target: start,
      weight: 0,
      planner,
      path: [],
      jump: false,
    };
  }

  /** Croix grise posée au sol à l'endroit d'une position ignorée. */
  private addGhost(at: Point3): void {
    const T = this.T;
    // Même recette que l'anneau du personnage : bandes plates posées au sol, double face.
    const material = new T.MeshBasicMaterial({
      color: 0x374151,
      transparent: true,
      opacity: 0.9,
      side: T.DoubleSide,
      clippingPlanes: [this.clip],
    });
    const obj = new T.Group();
    for (const angle of [Math.PI / 4, -Math.PI / 4]) {
      const bar = new T.Mesh(new T.PlaneGeometry(1.1, 0.18), material);
      bar.rotation.set(-Math.PI / 2, 0, angle);
      obj.add(bar);
    }
    obj.position.set(at.x, at.y + 0.05, at.z);
    this.peopleGroup.add(obj);
    this.ghosts.push({ obj, born: this.clock.elapsedTime });
  }

  private updateGhosts(): void {
    const now = this.clock.elapsedTime;
    this.ghosts = this.ghosts.filter(g => {
      const age = (now - g.born) / GHOST_S;
      const bars = g.obj.children as THREE.Mesh[];
      const material = bars[0].material as THREE.MeshBasicMaterial;
      if (age >= 1) {
        this.peopleGroup.remove(g.obj);
        bars.forEach(b => b.geometry.dispose());
        material.dispose();
        return false;
      }
      material.opacity = 0.9 * (1 - age);
      return true;
    });
  }

  private updatePeople(dt: number): void {
    this.updateGhosts();
    for (const p of this.people.values()) {
      if (p.jump) {
        p.pos.copy(p.target); // changement d'étage sans escalier dans le modèle : posé directement
        p.path = [];
        p.jump = false;
      }
      // Plus de vitesse plafond : le chemin restant se parcourt en `catchUpS` (réglage de démo),
      // coin après coin quand il contourne un mur, marche après marche dans l'escalier.
      const before = { x: p.pos.x, y: p.pos.y, z: p.pos.z };
      const step = followPath(before, p.path, dt, this.trajectory);
      p.path = step.path;
      p.pos.set(step.pos.x, step.pos.y, step.pos.z);
      const dx = p.pos.x - before.x;
      const dz = p.pos.z - before.z;
      const moving = step.speed > 0.15;
      if (moving && Math.hypot(dx, dz) > 1e-4) {
        p.obj.rotation.y = turnTo(p.obj.rotation.y, Math.atan2(dx, dz), Math.min(1, dt * 6));
      }
      p.weight += ((moving ? 1 : 0) - p.weight) * Math.min(1, dt * 5);
      // Marche jouée à la vitesse réelle : ni patinage quand il rattrape, ni marche sur place.
      p.walk.setEffectiveTimeScale(Math.min(2.5, Math.max(0.4, step.speed / WALK_SPEED_M_S)));
      p.walk.setEffectiveWeight(p.weight);
      p.idle.setEffectiveWeight(1 - p.weight);
      p.obj.position.copy(p.pos);
      p.ring.position.set(p.pos.x, p.pos.y + 0.03, p.pos.z);
      p.mixer.update(dt);
    }
  }
}

function turnTo(a: number, b: number, k: number): number {
  let d = b - a;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return a + d * k;
}

/** Triangles du plancher, des murs et des portes du GLB, classés par le nom du nœud (ou d'un parent). */
function navSourceOf(T: ThreeModule, root: THREE.Object3D): NavSource {
  root.updateMatrixWorld(true);
  const buckets = { floor: [] as number[], walls: [] as number[], doors: [] as number[], frames: [] as number[] };
  const stairs: number[][] = [];
  const v = new T.Vector3();
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) {
      return;
    }
    const names: string[] = [];
    for (let n: THREE.Object3D | null = mesh; n && n !== root; n = n.parent) {
      names.push(n.name);
    }
    const kind = navKindOf(names);
    if (!kind) {
      return;
    }
    const pos = mesh.geometry.getAttribute('position');
    const index = mesh.geometry.getIndex();
    const count = index ? index.count : pos.count;
    const out: number[] = kind === 'stairs' ? [] : buckets[kind];
    if (kind === 'stairs') {
      stairs.push(out);
    }
    for (let i = 0; i < count; i++) {
      v.fromBufferAttribute(pos, index ? index.getX(i) : i).applyMatrix4(mesh.matrixWorld);
      out.push(v.x, v.y, v.z);
    }
  });
  return {
    floor: Float32Array.from(buckets.floor),
    walls: Float32Array.from(buckets.walls),
    doors: Float32Array.from(buckets.doors),
    frames: Float32Array.from(buckets.frames),
    stairs: stairs.map(t => Float32Array.from(t)),
  };
}
